import React, { useState } from 'react';
import { telHref } from '../../shared/format';
import { BTN, BTN_PRIMARY } from '../liStyles';
import { useLocalActions } from '../LocalActions';

// Every way to act on an answer, one tap away and always visible (§4.7).
//   Call        opens the call panel (the number big, and ringing the desk phone);
//               a plain tel: link only where the page provides no call panel.
//   Send        sends the place to a guest's folio, or gives its code or card.
//   Book        "Book online" opens the booking link; "Call to book" opens the
//               booking-request form (onBook). A booking is logged when a request is
//               marked Booked — opening a link is not a booking, so it logs nothing.
//   Menu/Order  the place's own links. Directions: the maps link.
//   Share       copies the database's share text; logs `shared`.
// A button whose value is null is not drawn.
const openExternal = { target: '_blank', rel: 'noopener noreferrer' };

const AnswerActions = ({ answer, onBook, onOutcome, size = 'md' }) => {
  const [copied, setCopied] = useState(false);
  const { onCall, onSend } = useLocalActions();
  if (!answer) return null;
  const actions = answer.actions || {};
  const tel = telHref(actions.call || answer.phone);
  const bookLabel = actions.book;
  const bookOnline = Boolean(answer.booking_url) && bookLabel;
  const log = (outcome) => { if (onOutcome && answer.recommendation_id) onOutcome(answer.recommendation_id, outcome); };
  const btn = size === 'sm' ? `${BTN} !px-2.5 !py-1` : BTN;
  const primary = size === 'sm' ? `${BTN_PRIMARY} !px-2.5 !py-1` : BTN_PRIMARY;

  const share = async () => {
    if (!answer.share_text) return;
    try {
      await navigator.clipboard.writeText(answer.share_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      log('shared');
    } catch (e) {
      // Clipboard can be blocked by the browser; the text is still on screen to read out.
      // eslint-disable-next-line no-console
      console.warn('[local-intelligence] clipboard unavailable', e);
    }
  };

  const hasSecondary = Boolean(bookLabel || actions.menu || actions.order);
  return (
    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
      {/* Item 2 — action buttons. On mobile (<640px) the row collapses to a
          2×2 grid: Call + Directions on the first row, Send to guest + Share on
          the second. Secondary actions (Book / Call to book / Book online, Menu,
          Order) and the divider between groups are hidden below the sm
          breakpoint — they only render on desktop, so the mobile grid holds only
          the four primary buttons. At sm+ the row returns to a single
          flex-nowrap line with gap-1 and overflow-x-auto, unchanged from before.
          "Send to guest" keeps whitespace-nowrap so its label never wraps. */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-nowrap sm:items-center sm:gap-1 sm:overflow-x-auto">
        {tel && (onCall ? (
          <button type="button" className={`${primary} !px-2.5`} onClick={() => onCall(answer)}>Call</button>
        ) : (
          <a href={tel} className={`${primary} !px-2.5`} onClick={() => log('called')}>
            Call
          </a>
        ))}
        {actions.directions && <a href={actions.directions} {...openExternal} className={`${btn} !px-2.5`}>Directions</a>}
        {onSend && answer.entity_id && (
          <button type="button" className={`${btn} !px-2.5 whitespace-nowrap`} onClick={() => onSend(answer)}>Send to guest</button>
        )}
        {answer.share_text && (
          <button type="button" className={`${btn} !px-2.5`} onClick={share} aria-live="polite">
            {copied ? 'Copied' : 'Share'}
          </button>
        )}
        {hasSecondary && (
          <span className="mx-1 h-5 w-px shrink-0 bg-gray-200 dark:bg-gray-700 hidden sm:inline-block" aria-hidden="true" />
        )}
        {bookLabel && (bookOnline ? (
          <a href={answer.booking_url} {...openExternal} className={`${btn} !px-2.5 hidden sm:inline-flex`}>{bookLabel}</a>
        ) : (
          <button type="button" className={`${btn} !px-2.5 hidden sm:inline-flex`} onClick={() => onBook && onBook(answer)}>{bookLabel}</button>
        ))}
        {actions.menu && <a href={actions.menu} {...openExternal} className={`${btn} !px-2.5 hidden sm:inline-flex`}>Menu</a>}
        {actions.order && <a href={actions.order} {...openExternal} className={`${btn} !px-2.5 hidden sm:inline-flex`}>Order</a>}
      </div>
    </div>
  );
};

export default AnswerActions;
